import { DafTipNotice } from "./DafTipNotice/DafTipNotice";
import { FC, useCallback, useContext, useMemo, useRef } from "react";

import { Box, SelectChangeEvent, Stack, Tooltip } from "@mui/material";
import {
  DiscountType,
  FormType,
  buildFulfillmentCheckout,
  getTipAmountFromPercentage,
  getDollarValue,
  formatCurrencyAmount,
  formatSmartNarrowCurrencyFromCents,
  AvailablePaymentCurrency,
  FeatureFlagValue,
  StripeRecurringInterval,
} from "@simplyk/common";
import { Control, useWatch } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../../common/constants/amplitude";
import { useFrontendAuctionContext } from "../../../../../../common/contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../../../../common/contexts/FrontendFormContext";
import { useLocaleContext } from "../../../../../../common/contexts/LocaleContext";
import { getDiscountReduction, getRateSpecificDiscountAmount } from "../../../../../../common/helpers/order";
import {
  getAuctionBidAmounts,
  getOrderTaxTotals,
  getRateTaxMarkers,
  getTaxMarkersByTaxId,
} from "../../../../../../common/helpers/rates";
import { formatTaxRate } from "../../../../../../common/helpers/taxes";
import {
  DonationPaymentInput,
  useFormV2Context,
  useTicketingFormV2ContextOrDefaults,
} from "../../../contexts/FormV2Context";
import { PublicDonationCheckbox, usePublicDonationCheckboxRenderPosition } from "../PublicDonationCheckBox";
import { SectionWrapper } from "../SectionWrapper";

import { InHonour } from "./InHonour";
import { RepeatDonorTipLine } from "./RepeatDonorTipLine/RepeatDonorTipLine";
import { useEaseInCents } from "./RepeatDonorTipLine/helpers/useEaseInCents";
import { useRepeatDonorTipVariant } from "./RepeatDonorTipLine/helpers/useRepeatDonorTipLine";
import { TicketingRenewalAndDiscountsSection } from "./TicketingRenewalAndDiscountsSection";

import { AuctionPaymentInfoBox } from "@/canvas/features/FormV2/blocks/payment/SummarySection/AuctionPaymentInfoBox";
import { isRequired } from "@/common/helpers/validators";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { FormLearnMoreOnZeffyDialog } from "@/components/PaymentProcessor/FormLearnMoreOnZeffyDialog";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useTicketsDescriptions } from "@/components/PaymentProcessor/hooks/useTicketsDescriptions";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useZeffyDescription } from "@/components/PaymentProcessor/hooks/useZeffyDescription";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { getRenewalDate } from "@/components/PaymentProcessor/PaymentProcessorTotalWithTip";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- Public-form components consume the editor preview context.
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormChip } from "@/forms-design-system/FormChip";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormPaymentSummary } from "@/forms-design-system/FormPaymentSummary";
import { FormPaymentOrderItem } from "@/forms-design-system/FormPaymentSummary/FormPaymentOrderItem";
import { FormPaymentOrderSection } from "@/forms-design-system/FormPaymentSummary/FormPaymentOrderSection";
import { FormPaymentTipSection } from "@/forms-design-system/FormPaymentSummary/FormPaymentTipSection";
import { FormPaymentTipSelect } from "@/forms-design-system/FormPaymentSummary/FormPaymentTipSelect";
import { FormTextButton } from "@/forms-design-system/FormTextButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Add, Flash } from "@/icons/outlined";

const ASK_TO_CONFIRM_TIP_AMOUNT_THRESHOLD = 10000;

export const SummarySection: FC = () => {
  const { t } = useTranslate();
  const context = useFormV2Context();
  const { form, type, formObject, isAuction } = context;
  const commandFulfillment = context.type === FormType.Ticketing ? context.commandFulfillment : null;
  const { disabledAdvancedSetting } = usePreviewContext();
  const isSummaryGhostPreview = useIsGhostPreview("TicketingV2Step2SummarySection");
  const publicDonationCheckboxPosition = usePublicDonationCheckboxRenderPosition();
  const { logAmplitudeEvent } = useAmplitude();
  const freeTipInputRef = useRef<HTMLInputElement | null>(null);

  const { hasAlreadyBid, existingCommand, commandBidsRateIdsMap, highestBidsMapByRateId, activeAutoBidMaxByRateId } =
    useFrontendAuctionContext();
  const { isoLocale } = useLocaleContext();
  const {
    formType,
    stripeRecurrenceInterval,
    currency = AvailablePaymentCurrency.Usd,
    displayedFormAmount,
    suggestedTips,
    selectedTip,
    handleChangeFreeTips,
    freeTips,
    organization,
    logAmplitudeTipsChanged,
    handleChangeTips,
    getTipChoice,
    tip,
    formData,
    category,
    isSuggestedTipsAmount,
    displayTipSelection,
  } = useContext(FrontendFormContext);

  const {
    validDiscount,
    membershipRenewalBuyerInfo,
    ticketsPurchased,
    formObject: ticketing,
  } = useTicketingFormV2ContextOrDefaults();
  const { showZeffyDescription, openZeffyDescription, closeZeffyDescription } = useZeffyDescription();
  const repeatDonorTipVariant = useRepeatDonorTipVariant();

  const fulfillmentMethod = useWatch({
    control: form.control as Control<TicketingPaymentInput>,
    name: "fulfillmentMethod",
  });
  const { shippingFeeAmount: computedShippingFee } = buildFulfillmentCheckout({
    formCategory: ticketing.formCategory,
    offeredMethods: commandFulfillment?.offeredMethods ?? [],
    selectedMethod: fulfillmentMethod ?? null,
    ticketingShippingFeeAmount: commandFulfillment?.shippingFeeAmount ?? 0,
  });
  const auctionPersistedShippingFee = isAuction && hasAlreadyBid ? (existingCommand?.shippingFeeAmount ?? 0) : null;
  const shippingFeeAmount = auctionPersistedShippingFee ?? (commandFulfillment?.enabled ? computedShippingFee : 0);

  const summaryTotal = displayedFormAmount + tip + (isAuction ? shippingFeeAmount : 0);
  const animatedSummaryTotal = useEaseInCents(summaryTotal, repeatDonorTipVariant === FeatureFlagValue.C);

  const extraDonation = useWatch({ control: form.control as Control<TicketingPaymentInput>, name: "extraDonation" });
  const giveAsOrganism = useWatch({ control: form.control as Control<TicketingPaymentInput>, name: "giveAsOrganism" });
  const hasGiftAidConfirmation = useWatch({
    control: form.control as Control<TicketingPaymentInput>,
    name: "hasGiftAidConfirmation",
  });

  const discountFormattedReduction = useMemo(
    () => validDiscount && getDiscountReduction(validDiscount, isoLocale, currency),
    [currency, isoLocale, validDiscount]
  );
  const discountPrice =
    validDiscount && ticketsPurchased && ticketing.rates
      ? -getRateSpecificDiscountAmount(
          validDiscount,
          ticketsPurchased,
          ticketing.rates,
          validDiscount.selectedRateIds || []
        )
      : 0;
  const { ticketsDescriptions } = useTicketsDescriptions({ control: form.control as Control<TicketingPaymentInput> });

  const orderTaxTotals = useMemo(
    () =>
      getOrderTaxTotals({
        ticketsPurchased,
        rates: ticketing.rates || [],
        isAuction,
        discount: validDiscount,
        discountRateMappings: validDiscount?.selectedRateIds || [],
        auctionBids: isAuction
          ? getAuctionBidAmounts({
              ticketsPurchased,
              commandBidsRateIdsMap,
              highestBidsMapByRateId,
              activeAutoBidMaxByRateId,
              useAutoBidCeiling: true,
            })
          : undefined,
      }),
    [
      isAuction,
      ticketing.rates,
      ticketsPurchased,
      validDiscount,
      commandBidsRateIdsMap,
      highestBidsMapByRateId,
      activeAutoBidMaxByRateId,
    ]
  );
  const orderTaxAmount = orderTaxTotals.reduce((sum, taxTotal) => sum + taxTotal.amount, 0);
  const taxMarkersByTaxId = getTaxMarkersByTaxId(orderTaxTotals);
  const getTaxMarkersForRate = (rateId: string | undefined): string[] => {
    if (!rateId || orderTaxTotals.length === 0) {
      return [];
    }
    const rate = ticketing.rates?.find((candidate) => candidate.id === rateId);
    return getRateTaxMarkers(rate, taxMarkersByTaxId, isAuction);
  };

  const hasAnyAutoBidCeiling = isAuction && (ticketsDescriptions ?? []).some((t) => t.autoBidMaxAmount !== undefined);

  const recurringDonationSummary =
    formType === FormType.DonationForm && stripeRecurrenceInterval
      ? RECURRING_DONATION_SUMMARY[stripeRecurrenceInterval]
      : undefined;
  const summaryTitle = (() => {
    if (hasAlreadyBid) {
      return (
        <Stack direction="row" alignItems="center">
          <FormTypography variant="subtitle1" component="span" sx={{ marginRight: 0.5 }}>
            {t("ticketing", "activeBids")}
          </FormTypography>

          <DashboardInformationTooltip
            title={t("ticketing", "activeBidsTooltip")}
            slotProps={{ icon: { size: "small", vibe: "text-form-intense" } }}
          />
        </Stack>
      );
    }
    if (isAuction) {
      return t("ticketing", "totalBids");
    }
    if (formType === FormType.Ticketing) {
      return orderTaxTotals.length > 0 ? t("ticketing", "payment.summary.orderSubtotal") : t("ticketing", "order");
    }

    if (recurringDonationSummary) {
      return t("donationForm", recurringDonationSummary.titleKey);
    }
    return <>{t("donationForm", "donation")}</>;
  })();

  const onChangeTip = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      logAmplitudeEvent(AmplitudeEvents.DonorFormTipChanged, {
        amount: getDollarValue(displayedFormAmount),
        form_id: formData.id,
        form_type: category,
        organization_country: organization.country,
        tip_choice: getTipChoice(event.target.value as string),
        tip_amount: getDollarValue(getTipAmountFromPercentage(displayedFormAmount, Number(event.target.value))),
      });
      setTimeout(() => {
        if (event.target.value === "free" && freeTipInputRef?.current) {
          freeTipInputRef.current.focus();
        }
      }, 100);
      handleChangeTips(event);
    },
    [
      logAmplitudeEvent,
      displayedFormAmount,
      formData.id,
      category,
      organization.country,
      getTipChoice,
      handleChangeTips,
    ]
  );

  const chargeDay = useWatch({ control: form.control as Control<DonationPaymentInput>, name: "chargeDay" });
  const renewalDate = getRenewalDate(membershipRenewalBuyerInfo, stripeRecurrenceInterval, formType, chargeDay);

  const shouldAskToConfirmTipAmount = tip >= displayedFormAmount && tip >= ASK_TO_CONFIRM_TIP_AMOUNT_THRESHOLD;
  const swiftaidEligibleAmount = type === FormType.DonationForm ? displayedFormAmount : extraDonation || 0;

  const tipAmountConfirmationCheckbox = shouldAskToConfirmTipAmount ? (
    <FormCheckbox
      control={form.control as Control<TicketingPaymentInput | DonationPaymentInput>}
      name="confirmTipAmount"
      rules={[isRequired(shouldAskToConfirmTipAmount, t("ticketing", "confirmTipAmountRequired"))]}
      size="small"
      label={t("ticketing", "confirmTipAmount")}
      error={!!form.formState.errors.confirmTipAmount}
      helperText={form.formState.errors.confirmTipAmount?.message}
      data-test="confirm-tip-amount-checkbox"
    />
  ) : undefined;

  return (
    <SectionWrapper title={t("ticketing", "summary")} isGhostPreview={isSummaryGhostPreview}>
      <Stack spacing={2}>
        {publicDonationCheckboxPosition === "summary-section" && <PublicDonationCheckbox />}
        {type === FormType.DonationForm &&
          (formObject.allowInHonour || disabledAdvancedSetting === "allowInHonour") && <InHonour />}

        {type === FormType.Ticketing && <TicketingRenewalAndDiscountsSection />}

        {isAuction && <AuctionPaymentInfoBox />}

        <FormLearnMoreOnZeffyDialog open={showZeffyDescription} onClose={closeZeffyDescription} />

        <FormPaymentSummary
          currency={currency}
          renewalDate={renewalDate}
          timezone={ticketing.eventTimezone}
          totalPrefix={hasAnyAutoBidCeiling ? t("ticketing", "auctionV2.upToPrefix") : undefined}
          total={animatedSummaryTotal}
          slots={{
            order: (
              <FormPaymentOrderSection
                title={summaryTitle}
                subtotal={displayedFormAmount - orderTaxAmount}
                currency={currency}
                recurrence={recurringDonationSummary?.recurrence}
              >
                {ticketsDescriptions?.map((tickets, index) => {
                  const taxMarkers = getTaxMarkersForRate(tickets.rateId);
                  const autoBidPriceLabel =
                    tickets.autoBidMaxAmount !== undefined ? (
                      <Stack direction="row" gap={0.5} alignItems="center" sx={{ whiteSpace: "nowrap" }}>
                        <FormIcon
                          size="small"
                          color="currentColor"
                          sx={(theme) => ({ color: theme.palette.forms?.text.brand })}
                        >
                          <Flash />
                        </FormIcon>
                        <FormTypography variant="body2">
                          {t("ticketing", "auctionV2.upToPrefix")}{" "}
                          {formatSmartNarrowCurrencyFromCents(tickets.autoBidMaxAmount, {
                            locale: isoLocale,
                            currency,
                          })}
                        </FormTypography>
                      </Stack>
                    ) : null;

                  return (
                    <FormPaymentOrderItem
                      key={index}
                      label={
                        <Stack sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              minWidth: 0,
                              columnGap: 1,
                              rowGap: 0.25,
                            }}
                          >
                            {tickets.isAddOn && (
                              <FormIcon
                                size="small"
                                sx={(theme) => ({ color: theme.palette.forms.text.brand })}
                                data-test="add-on-line-prefix"
                              >
                                <Add />
                              </FormIcon>
                            )}
                            <Tooltip title={tickets.description} enterTouchDelay={0}>
                              <FormTypography
                                variant="body2"
                                component="span"
                                vibe="moderate"
                                data-test="ticket-description"
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  minWidth: 0,
                                  flex: "0 1 auto",
                                }}
                              >
                                {tickets.description}
                              </FormTypography>
                            </Tooltip>
                            {taxMarkers.length > 0 && (
                              <FormTypography
                                variant="body2"
                                component="span"
                                vibe="quiet"
                                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                                data-test="ticket-tax-markers"
                              >
                                {t("ticketing", "payment.summary.taxApplies", {
                                  markers: taxMarkers.join(", "),
                                  count: taxMarkers.length,
                                })}
                              </FormTypography>
                            )}
                            {tickets.isNew && (
                              <Box component="span" sx={{ flexShrink: 0 }}>
                                <FormChip vibe="neutral" variant="outlined" label={t("ticketing", "newBid")} />
                              </Box>
                            )}
                            {tickets.recurrenceExpirationInfo && (
                              <FormTypography variant="subtitle2" component="span" vibe="supershy" sx={{ minWidth: 0 }}>
                                {tickets.recurrenceExpirationInfo}
                              </FormTypography>
                            )}
                          </Box>
                          {tickets.variantLines?.map((variantLine, variantIndex) => (
                            <FormTypography
                              key={variantIndex}
                              variant="body2"
                              component="li"
                              vibe="moderate"
                              sx={{ listStyleType: "disc", marginLeft: 2.5 }}
                            >
                              {variantLine.label}
                            </FormTypography>
                          ))}
                        </Stack>
                      }
                      price={autoBidPriceLabel ?? tickets.price}
                      currency={currency}
                    />
                  );
                })}
                {validDiscount && (
                  <FormPaymentOrderItem
                    label={
                      t("ticketing", "discountValue") +
                      (validDiscount?.type === DiscountType.Percentage ? " " + (discountFormattedReduction || "") : "")
                    }
                    price={discountPrice}
                    currency={currency}
                  />
                )}
                {shippingFeeAmount > 0 && (
                  <FormPaymentOrderItem
                    label={t("ticketing", "fulfillment.shippingFee")}
                    price={shippingFeeAmount}
                    currency={currency}
                  />
                )}
                {(extraDonation ?? 0) > 0 && (
                  <FormPaymentOrderItem
                    label={t("ticketing", "extraDonation")}
                    price={extraDonation}
                    currency={currency}
                  />
                )}
                {orderTaxTotals.map((taxTotal) => (
                  <FormPaymentOrderItem
                    key={taxTotal.taxId}
                    label={
                      <Stack direction="row" gap={1} sx={{ minWidth: 0 }}>
                        <FormTypography variant="body2" component="span" vibe="quiet" sx={{ flexShrink: 0 }}>
                          {`(${taxMarkersByTaxId.get(taxTotal.taxId)})`}
                        </FormTypography>
                        <FormTypography variant="body2" component="span" vibe="moderate">
                          {`${taxTotal.name} (${formatTaxRate(taxTotal.ratePpm, isoLocale)})`}
                        </FormTypography>
                      </Stack>
                    }
                    price={taxTotal.amount}
                    currency={currency}
                    data-test="order-tax-line"
                  />
                ))}
                {repeatDonorTipVariant && <RepeatDonorTipLine tipAmountConfirmation={tipAmountConfirmationCheckbox} />}
                {hasGiftAidConfirmation && !giveAsOrganism && (
                  <FormTypography variant="body2" vibe="brand">
                    {t("ticketing", "extraDonationWithSwiftAid", {
                      amount: formatCurrencyAmount({
                        value: getDollarValue(0.25 * swiftaidEligibleAmount),
                        locale: isoLocale,
                        currency,
                      }),
                    })}
                  </FormTypography>
                )}
              </FormPaymentOrderSection>
            ),
            tip: (
              <>
                <DafTipNotice />
                {displayTipSelection && !repeatDonorTipVariant ? (
                  <FormPaymentTipSection
                    organizationName={organization.name}
                    organizationCountry={organization.country}
                    isSuggestedTipsAmount={isSuggestedTipsAmount}
                    slots={{
                      learnMore: (
                        <FormTextButton onClick={openZeffyDescription}>{t("donationForm", "knowMore")}</FormTextButton>
                      ),
                      tipSelect: (
                        <FormPaymentTipSelect
                          value={selectedTip}
                          options={suggestedTips}
                          currency={currency}
                          onChange={onChangeTip}
                          onChangeFreeTips={handleChangeFreeTips}
                          freeTips={freeTips}
                          onBlurTip={logAmplitudeTipsChanged}
                          freeTipInputRef={freeTipInputRef}
                          isAuction={isAuction}
                          isSuggestedTipsAmount={isSuggestedTipsAmount}
                          organizationCountry={organization.country}
                        />
                      ),
                      tipAmountConfirmation: tipAmountConfirmationCheckbox,
                    }}
                    helperText={
                      isAuction
                        ? selectedTip.isFree
                          ? t("ticketing", "auction.payment.tip.customHelper")
                          : t("ticketing", "auction.payment.tip.helper")
                        : undefined
                    }
                  />
                ) : undefined}
              </>
            ),
          }}
        />
      </Stack>
    </SectionWrapper>
  );
};

const RECURRING_DONATION_SUMMARY: Partial<
  Record<
    StripeRecurringInterval,
    {
      titleKey:
        | "weeklyDonationCancelAnytime"
        | "biWeeklyDonationCancelAnytime"
        | "monthlyDonationCancelAnytime"
        | "quarterlyDonationCancelAnytime"
        | "yearlyDonationCancelAnytime";
      recurrence: "weekly" | "biWeekly" | "monthly" | "quarterly" | "yearly";
    }
  >
> = {
  [StripeRecurringInterval.week]: { titleKey: "weeklyDonationCancelAnytime", recurrence: "weekly" },
  [StripeRecurringInterval.biWeek]: { titleKey: "biWeeklyDonationCancelAnytime", recurrence: "biWeekly" },
  [StripeRecurringInterval.month]: { titleKey: "monthlyDonationCancelAnytime", recurrence: "monthly" },
  [StripeRecurringInterval.quarter]: { titleKey: "quarterlyDonationCancelAnytime", recurrence: "quarterly" },
  [StripeRecurringInterval.year]: { titleKey: "yearlyDonationCancelAnytime", recurrence: "yearly" },
};
